import { useState, useRef, useEffect } from 'react';
import { Plane, Eye, EyeOff, ShieldCheck, Loader2 } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '../../context/AuthContext';
import { track } from '../../analytics';

interface SignUpScreenProps {
  onSwitchToLogin: () => void;
}

export function SignUpScreen({ onSwitchToLogin }: SignUpScreenProps) {
  const { signUp, verifyOtp, resendOtp, signInWithApple } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'form' | 'otp'>('form');

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim() || !password || !confirmPassword) {
      setError('Please fill in all fields.');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    const result = await signUp(email.trim(), password);
    setLoading(false);

    if (result.error) {
      setError(result.error);
    } else {
      track('signup_submitted');
      setStep('otp');
    }
  };

  const handleAppleSignIn = async () => {
    setError(null);
    setLoading(true);
    const result = await signInWithApple();
    setLoading(false);

    if (result.error) {
      setError(result.error);
    }
  };

  if (step === 'otp') {
    return (
      <OtpVerifyScreen
        email={email.trim()}
        verifyOtp={verifyOtp}
        resendOtp={resendOtp}
        onBack={() => { setStep('form'); setError(null); }}
      />
    );
  }

  return (
    <div className="min-h-dvh bg-slate-900 flex flex-col items-center justify-center px-6">
      <div className="flex flex-col items-center mb-10">
        <div className="w-16 h-16 rounded-2xl bg-slate-800 border border-slate-700 flex items-center justify-center mb-4">
          <Plane size={32} className="text-accent" />
        </div>
        <h1 className="text-2xl font-bold text-slate-100">Rost & Found</h1>
        <p className="text-slate-400 text-sm mt-1">Create your account</p>
      </div>

      <form onSubmit={handleSignUp} className="w-full max-w-sm space-y-4">
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-red-400 text-sm">
            {error}
          </div>
        )}

        <div>
          <label htmlFor="signup-email" className="block text-sm font-medium text-slate-400 mb-1.5">
            Email
          </label>
          <input
            id="signup-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            className="w-full px-4 py-3 rounded-lg bg-slate-700 border border-slate-600 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-colors"
          />
        </div>

        <div>
          <label htmlFor="signup-password" className="block text-sm font-medium text-slate-400 mb-1.5">
            Password
          </label>
          <div className="relative">
            <input
              id="signup-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              className="w-full px-4 py-3 rounded-lg bg-slate-700 border border-slate-600 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-colors pr-12"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>

        <div>
          <label htmlFor="signup-confirm" className="block text-sm font-medium text-slate-400 mb-1.5">
            Confirm Password
          </label>
          <input
            id="signup-confirm"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Re-enter your password"
            className="w-full px-4 py-3 rounded-lg bg-slate-700 border border-slate-600 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-colors"
          />
        </div>

        <p className="text-xs text-slate-500 text-center">
          By creating an account, you agree to our{' '}
          <a href="/terms.html" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">Terms of Service</a>
          {' '}and{' '}
          <a href="/privacy.html" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">Privacy Policy</a>.
        </p>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 rounded-lg bg-accent text-white font-semibold text-base hover:bg-accent-dark active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Creating account...' : 'Create Account'}
        </button>

        {Capacitor.isNativePlatform() && (
          <button
            type="button"
            onClick={handleAppleSignIn}
            disabled={loading}
            className="w-full py-3 rounded-lg bg-black text-white font-medium text-base flex items-center justify-center gap-2 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed border border-slate-600"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path
                d="M13.1 9.45c-.02-1.95 1.6-2.89 1.67-2.93a3.63 3.63 0 00-2.86-1.54c-1.2-.13-2.38.72-2.99.72-.62 0-1.56-.7-2.57-.68a3.78 3.78 0 00-3.18 1.94c-1.37 2.37-.35 5.86.97 7.78.66.94 1.43 1.99 2.44 1.95 1-.04 1.37-.63 2.57-.63 1.19 0 1.53.63 2.57.61 1.06-.02 1.72-.94 2.36-1.89a7.92 7.92 0 001.08-2.2c-.02-.01-2.06-.79-2.08-3.13zM11.15 3.62A3.7 3.7 0 0012 1a3.77 3.77 0 00-2.43 1.26 3.5 3.5 0 00-.88 2.54 3.12 3.12 0 002.46-1.18z"
                fill="currentColor"
              />
            </svg>
            Sign in with Apple
          </button>
        )}

        <div className="relative py-2">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-slate-700" />
          </div>
        </div>

        <p className="text-center text-sm text-slate-400">
          Already have an account?{' '}
          <button
            type="button"
            onClick={onSwitchToLogin}
            className="text-accent font-medium hover:underline"
          >
            Sign in
          </button>
        </p>
      </form>
    </div>
  );
}

// ── OTP Verification Screen ──

const CODE_LENGTH = 6;

function OtpVerifyScreen({
  email,
  verifyOtp,
  resendOtp,
  onBack,
}: {
  email: string;
  verifyOtp: (email: string, token: string) => Promise<{ error: string | null }>;
  resendOtp: (email: string) => Promise<{ error: string | null }>;
  onBack: () => void;
}) {
  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const [error, setError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [resent, setResent] = useState(false);
  const [resending, setResending] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (index: number, value: string) => {
    // Only accept digits
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[index] = digit;
    setDigits(next);
    setError(null);

    // Auto-advance to next input
    if (digit && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all filled
    if (digit && index === CODE_LENGTH - 1 && next.every((d) => d)) {
      submitCode(next.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (!pasted) return;

    const next = [...digits];
    for (let i = 0; i < pasted.length; i++) {
      next[i] = pasted[i];
    }
    setDigits(next);

    // Focus the next empty or last input
    const focusIdx = Math.min(pasted.length, CODE_LENGTH - 1);
    inputRefs.current[focusIdx]?.focus();

    if (next.every((d) => d)) {
      submitCode(next.join(''));
    }
  };

  const submitCode = async (code: string) => {
    setVerifying(true);
    setError(null);
    const result = await verifyOtp(email, code);
    setVerifying(false);

    if (result.error) {
      setError(result.error);
      // Clear and refocus
      setDigits(Array(CODE_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
    }
    // On success, AuthContext handles the session change automatically
  };

  const handleResend = async () => {
    setResending(true);
    setError(null);
    const result = await resendOtp(email);
    setResending(false);

    if (result.error) {
      setError(result.error);
    } else {
      setResent(true);
      setTimeout(() => setResent(false), 4000);
    }
  };

  const code = digits.join('');
  const canSubmit = code.length === CODE_LENGTH && !verifying;

  return (
    <div className="min-h-dvh bg-slate-900 flex flex-col items-center justify-center px-6">
      <div className="flex flex-col items-center text-center max-w-sm">
        <div className="w-16 h-16 rounded-2xl bg-slate-800 border border-slate-700 flex items-center justify-center mb-6">
          <ShieldCheck size={32} className="text-accent" />
        </div>

        <h2 className="text-xl font-bold text-slate-100 mb-2">Verify your email</h2>
        <p className="text-slate-400 text-sm mb-8">
          Enter the 6-digit code sent to{' '}
          <span className="text-slate-200 font-medium">{email}</span>
        </p>

        {error && (
          <div className="w-full bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-red-400 text-sm mb-4">
            {error}
          </div>
        )}

        {/* OTP input boxes */}
        <div className="flex gap-2.5 mb-6" onPaste={handlePaste}>
          {digits.map((digit, i) => (
            <input
              key={i}
              ref={(el) => { inputRefs.current[i] = el; }}
              type="text"
              inputMode="numeric"
              autoComplete={i === 0 ? 'one-time-code' : 'off'}
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              disabled={verifying}
              className={`w-11 h-14 text-center text-xl font-bold rounded-lg border-2 bg-slate-800 text-slate-100 outline-none transition-all ${
                digit
                  ? 'border-accent/60'
                  : 'border-slate-600 focus:border-accent/80'
              } disabled:opacity-50`}
            />
          ))}
        </div>

        {/* Verify button */}
        <button
          onClick={() => canSubmit && submitCode(code)}
          disabled={!canSubmit}
          className="w-full py-3 rounded-lg bg-accent text-white font-semibold text-base hover:bg-accent-dark active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {verifying ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Verifying...
            </>
          ) : (
            'Verify'
          )}
        </button>

        {/* Resend + Back */}
        <div className="flex flex-col items-center gap-3 mt-6">
          <button
            onClick={handleResend}
            disabled={resending || resent}
            className="text-sm text-accent font-medium hover:underline disabled:opacity-50 disabled:no-underline"
          >
            {resent ? 'Code resent!' : resending ? 'Sending...' : "Didn't get the code? Resend"}
          </button>
          <button
            onClick={onBack}
            className="text-sm text-slate-500 hover:text-slate-300"
          >
            Back
          </button>
        </div>
      </div>
    </div>
  );
}
